import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverSocialSelling from "@/assets/cover-social-selling.jpg";
import postLinkedin from "@/assets/post-social-selling-linkedin.jpg";
import postConteudo from "@/assets/post-social-selling-conteudo.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postCoverColdcalling from "@/assets/cover-cold-calling.jpg";
import postCoverComoVenderMais from "@/assets/cover-como-vender-mais.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  MessageCircle,
  BarChart3,
  Star,
  AlertTriangle,
  Lightbulb,
  Award,
  Search,
  Calendar,
  TrendingUp,
  Briefcase,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const SocialSellingLinkedIn2026 = () => {
  const navigate = useNavigate();

  const checklist = [
    {
      number: "01",
      title: "Reposicione Seu Perfil Como Página de Conversão — Não Como Currículo",
      icon: Briefcase,
      problem: "Seu LinkedIn ainda parece um CV digital com 'apaixonado por vendas' no headline?",
      action:
        "O perfil é sua landing page. Em 2026, o decisor B2B faz uma média de 7 visitas ao seu perfil antes de aceitar uma reunião. Headline precisa responder: para quem você resolve qual dor e com que resultado. Foto profissional, banner com proposta de valor, sobre escrito em primeira pessoa com prova social. Cada elemento precisa empurrar o visitante para o próximo passo: aceitar a conexão, responder a mensagem ou marcar a call. Perfil amador queima oportunidade antes mesmo da primeira interação.",
      checkItems: [
        "Reescreva o headline no formato: 'Ajudo [ICP] a [resultado] sem [dor comum]'",
        "Suba uma foto profissional com fundo neutro e expressão acessível",
        "Crie um banner com proposta de valor clara e prova social numérica",
        "Reescreva a seção 'Sobre' em primeira pessoa contando dor → método → resultado",
        "Adicione 3 a 5 depoimentos de clientes na seção de recomendações",
      ],
      story: null,
    },
    {
      number: "02",
      title: "Defina o ICP no LinkedIn Antes de Apertar 'Conectar'",
      icon: Target,
      problem: "Você manda convite para qualquer cargo que parece próximo do seu cliente?",
      action:
        "Social Selling sem ICP é spam disfarçado de networking. Antes de qualquer ação, monte uma persona objetiva: setor, porte da empresa, cargo, senioridade, região e gatilho de compra. Em 2026 o LinkedIn pune algoritmicamente quem manda convite genérico — sua taxa de aceite cai e seu alcance orgânico despenca. ICP claro multiplica resposta por 4 e protege o domínio do seu perfil.",
      checkItems: [
        "Liste seus 10 melhores clientes e identifique padrões de cargo, setor e porte",
        "Documente 3 critérios obrigatórios e 3 desejáveis para conectar",
        "Crie 2 ou 3 personas distintas (decisor técnico, decisor financeiro, influenciador)",
        "Proíba convites para fora do ICP — qualidade preserva o algoritmo",
      ],
      story: null,
    },
    {
      number: "03",
      title: "Use o Sales Navigator Como Ferramenta de Inteligência, Não Lista de Telefone",
      icon: Search,
      problem: "Você assinou Sales Navigator e usa só a busca avançada?",
      action:
        "Sales Navigator em 2026 oferece sinais de compra reais: mudança de cargo, novo round de investimento, contratação acelerada, menção em notícias, intenção de compra na rede. Quem usa só a busca está pagando R$ 600 por mês por uma agenda telefônica cara. Configure alertas, listas dinâmicas e TeamLink. Cada sinal é um gatilho para abordagem contextual — e abordagem contextual fecha 7x mais do que mensagem fria.",
      checkItems: [
        "Configure pelo menos 5 alertas de gatilhos por persona (job change, posts, menções)",
        "Crie listas dinâmicas que se atualizam automaticamente conforme o ICP muda",
        "Use a função TeamLink para identificar conexões em 2º grau com o decisor",
        "Reserve 30 minutos por dia só para revisar alertas — esse é seu motor de prospecção",
      ],
      story: null,
    },
    {
      number: "04",
      title: "Conecte Com Contexto — Adeus ao Convite Em Branco",
      icon: Users,
      problem: "Você ainda clica em 'Conectar' sem mandar mensagem personalizada?",
      action:
        "Em 2026, convite sem mensagem tem taxa de aceite de 18%. Convite com mensagem contextual atinge 62%. A mensagem ideal cita um gatilho real (post, evento, conexão em comum, notícia da empresa) e fecha sem pedir nada — só com convite à conversa. Mensagem que pede reunião no primeiro toque é descartada na hora. Conectar é construir relacionamento; vender é etapa seguinte. Confunda os dois e o algoritmo te marca como spam.",
      checkItems: [
        "Crie um template de convite com 3 campos obrigatórios de personalização",
        "Limite-se a 20 a 30 convites por dia para preservar a saúde da conta",
        "Mencione gatilho real (post recente, mudança de cargo, evento, contato em comum)",
        "Nunca peça reunião no convite — apenas convide para conexão",
        "Acompanhe sua taxa de aceite semanalmente — abaixo de 40% revisa o template",
      ],
      story: `Joice Aparecida de Oliveira, da Mapfre Seguros, mudou totalmente seu jogo quando passou a personalizar cada convite. Saiu de uma média de 12% de aceite para mais de 55% em poucas semanas. "Eu consegui realizar 4 vendas de um produto que ninguém conseguiu vender na empresa, graças as técnicas e estratégias que aprendi no treinamento." Personalização não é detalhe — é a diferença entre ser visto como parceiro ou como mais um vendedor invasivo.`,
    },
    {
      number: "05",
      title: "Construa Autoridade Postando 3 Vezes Por Semana — Sempre",
      icon: Lightbulb,
      problem: "Você posta uma vez por mês e fica chateado que ninguém comenta?",
      action:
        "O algoritmo do LinkedIn em 2026 prioriza consistência. Postar 3 vezes por semana de forma constante gera 5x mais alcance do que postar diariamente por uma semana e sumir por um mês. Conteúdo precisa rodar em três pilares: ensinar (postagem de método), inspirar (case ou opinião provocativa) e conectar (pergunta para a audiência). Posts não vendem diretamente — eles aquecem o decisor para responder seu DM. Quem aparece toda semana vira referência. Quem some é esquecido.",
      checkItems: [
        "Defina 3 dias e horários fixos de publicação (ex: ter/qui/sáb 8h)",
        "Crie um calendário editorial mensal com pilares (ensina, inspira, conecta)",
        "Use a regra 80/20: 80% de valor, 20% de chamada para ação suave",
        "Reserve 30 min antes de postar para preparar texto, imagem e os primeiros 3 comentários",
        "Evite links externos no corpo do post — o algoritmo penaliza, coloque no 1º comentário",
      ],
      story: null,
    },
    {
      number: "06",
      title: "Domine o Formato Que Bomba: Texto Longo Com Storytelling",
      icon: BookOpen,
      problem: "Você posta meme genérico e frases motivacionais e não entende por que não engaja?",
      action:
        "Em 2026, o formato com maior dwell time no LinkedIn é texto longo com storytelling pessoal — entre 1.200 e 1.800 caracteres. Estrutura comprovada: gancho de 2 linhas (quebra de padrão), contexto, conflito, virada, aprendizado, pergunta final. Carrosséis também bombam para conteúdo educacional. Vídeo curto cresceu, mas exige produção. Comece pelo texto — é o formato mais democrático e o que mais converte para DM. Memes genéricos são lixo algorítmico: alto alcance, zero pipeline.",
      checkItems: [
        "Use as primeiras 2 linhas como gancho — elas decidem se o leitor expande ou rola",
        "Conte uma história real com nome, contexto e dado numérico",
        "Termine com uma pergunta aberta que provoque comentário (não um sim/não)",
        "Edite cortando 30% do texto antes de publicar — menos é mais",
        "Responda cada comentário nos primeiros 60 minutos para amplificar o alcance",
      ],
      story: `Gabriele Aparecida da Cruz, da BRINQPET, descobriu na prática o poder do storytelling estruturado. "O curso foi prático, dinâmico e extremamente didático. Hoje vejo o mundo das vendas com outros olhos." Contar a história real de um cliente — com o nome, a dor e o número — gera identificação muito maior do que qualquer post genérico sobre 'mindset de vencedor'.`,
    },
    {
      number: "07",
      title: "Engage Estrategicamente — Comentários Bem Feitos Geram Mais Pipeline Que Posts",
      icon: MessageCircle,
      problem: "Você comenta 'top demais!' nos posts dos decisores e acha que está construindo relação?",
      action:
        "Comentário inteligente em post de decisor é prospecção disfarçada de valor. Em 2026, com 1 hora por dia de engajamento estratégico em 20 perfis-chave do seu ICP, você gera mais reunião do que com 100 mensagens privadas. Comentário ideal: agrega informação, conta micro-case, faz pergunta provocativa ou discorda com respeito. Visibilidade na rede do decisor te transforma de 'desconhecido' em 'aquele profissional que sempre comenta com inteligência'. Quando você manda o DM, ele já te reconhece.",
      checkItems: [
        "Selecione 20 perfis prioritários do ICP e ative o sino de notificações",
        "Comente nos primeiros 30 min após a publicação para máxima visibilidade",
        "Proíba comentários genéricos ('Top!', 'Concordo total', 'Excelente reflexão')",
        "Cada comentário deve agregar dado, exemplo ou pergunta — nunca elogio vazio",
        "Acompanhe quantos comentários geraram DM ou solicitação de conexão",
      ],
      story: null,
    },
    {
      number: "08",
      title: "Inicie Conversas no DM Com a Regra do 3:1",
      icon: MessageCircle,
      problem: "Sua primeira mensagem após o aceite já manda link da empresa e pede reunião?",
      action:
        "A regra de ouro é 3:1 — três interações de valor antes de qualquer pitch. Mensagem 1: agradecer a conexão e fazer uma pergunta aberta sobre o trabalho do prospect. Mensagem 2 (3 a 5 dias depois): compartilhar um conteúdo útil sem pedir nada. Mensagem 3 (semana seguinte): comentar algo específico do post mais recente dele. Só então, na quarta interação, sugerir uma conversa rápida. Esse ritmo respeita o tempo do decisor e converte 8x mais que o pitch direto. Pressa mata conversão.",
      checkItems: [
        "Documente um fluxo de DM em 4 etapas no seu CRM",
        "Personalize cada mensagem com algo específico (cargo, setor, post, gatilho)",
        "Evite áudios na primeira interação — eles são invasivos para quem não te conhece",
        "Faça pergunta aberta para iniciar diálogo (nunca pergunta que termina em sim/não)",
        "Meça taxa de resposta por etapa — ajuste o fluxo onde a queda for maior",
      ],
      story: null,
    },
    {
      number: "09",
      title: "Use Eventos Como Atalho Para Conhecer Decisores",
      icon: Calendar,
      problem: "Você ignora os eventos do LinkedIn porque acha que são webinar genérico?",
      action:
        "Eventos no LinkedIn em 2026 são o atalho mais subutilizado do Social Selling. Quando você se inscreve em um evento, a plataforma libera a lista de inscritos — todos visíveis com cargo e empresa. É inteligência comercial pronta. Melhor ainda: organize seus próprios eventos com convidados estratégicos. Quem participa do seu evento aceita 3x mais conexão depois. Decisor que ignora cold message responde quando você o convida para um painel.",
      checkItems: [
        "Inscreva-se em 2 eventos por semana dentro do seu nicho",
        "Mapeie a lista de inscritos antes do evento e conecte com mensagem contextual",
        "Organize seu próprio evento trimestral com 2 ou 3 convidados do ICP",
        "Pós-evento: envie material exclusivo aos participantes via DM",
        "Use o status 'Participando' como gancho para mensagem ('vi que você vai também')",
      ],
      story: null,
    },
    {
      number: "10",
      title: "Aplique Social Listening Para Encontrar Dores Reais",
      icon: Search,
      problem: "Você prospecta no escuro sem saber o que o decisor está reclamando publicamente?",
      action:
        "Social Listening transforma o LinkedIn em uma base infinita de gatilhos de venda. Decisor reclamou de um fornecedor? Gatilho. Postou sobre meta não batida? Gatilho. Compartilhou um relatório do setor? Gatilho para mandar análise complementar. Em 2026 quem domina escuta ativa nas redes vira consultor antes de virar vendedor. A abordagem deixa de ser 'olá, tenho um produto' e vira 'vi seu post sobre X, posso compartilhar como resolvi isso com outro cliente?'.",
      checkItems: [
        "Salve buscas no Sales Navigator com palavras-chave de dor do seu ICP",
        "Crie alertas Google para os 30 principais clientes potenciais",
        "Dedique 15 min por dia para revisar posts dos decisores do ICP",
        "Documente os gatilhos em um campo do CRM para usar na abordagem",
        "Responda públicamente quando o gatilho for relevante — depois leve para o DM",
      ],
      story: null,
    },
    {
      number: "11",
      title: "Meça SSI, Taxa de Aceite, Reply Rate e Pipeline Gerado",
      icon: BarChart3,
      problem: "Você posta, conecta e manda mensagem sem saber se está funcionando?",
      action:
        "Sem métrica, Social Selling vira hobby. Em 2026 as quatro métricas que importam são: SSI (Social Selling Index do próprio LinkedIn, acima de 70 é referência), taxa de aceite de conexão (meta acima de 50%), reply rate no DM (meta acima de 25%) e pipeline gerado em valor (meta varia por ticket). Acompanhe semanalmente em um dashboard simples. Métrica vaidade (visualização de post, like) não paga conta — pipeline gerado, sim.",
      checkItems: [
        "Cheque seu SSI semanalmente em linkedin.com/sales/ssi",
        "Crie planilha com taxa de aceite, reply rate e reuniões agendadas por semana",
        "Marque no CRM toda oportunidade originada por LinkedIn",
        "Compare CAC do Social Selling versus outros canais a cada trimestre",
        "Revise as métricas em ritual semanal com a liderança comercial",
      ],
      story: null,
    },
    {
      number: "12",
      title: "Integre LinkedIn ao CRM e à Cadência Multicanal",
      icon: TrendingUp,
      problem: "O que acontece no LinkedIn fica no LinkedIn — sem rastro no CRM?",
      action:
        "Em 2026, LinkedIn isolado é canal limitado. Integrado ao CRM (HubSpot, Pipedrive, Salesforce) via Sales Navigator ou ferramentas como Reev, Meetime, Surfe e Apollo, vira parte de uma cadência multicanal poderosa: ligação + e-mail + LinkedIn + WhatsApp. Cada toque agrega contexto e aumenta a chance de resposta. Lead que ignora e-mail responde no LinkedIn; lead que ignora LinkedIn atende a ligação. O Social Selling deixa de ser silo e vira motor do funil.",
      checkItems: [
        "Conecte Sales Navigator ao CRM da operação",
        "Documente cadência multicanal com pelo menos 3 toques de LinkedIn",
        "Crie automação para registrar interações do LinkedIn no histórico do lead",
        "Treine SDR e closer no mesmo padrão de mensagem entre canais",
        "Audite mensalmente onde a queda de conversão é maior e ajuste a sequência",
      ],
      story: null,
    },
    {
      number: "13",
      title: "Mantenha Disciplina — Social Selling É Maratona, Não Sprint",
      icon: Award,
      problem: "Você posta 5 vezes na primeira semana e some por dois meses?",
      action:
        "O maior inimigo do Social Selling é o entusiasmo sem ritmo. Resultado real aparece entre o 3º e o 6º mês de constância. Quem desiste no 45º dia jamais saberia que estava a 30 dias do primeiro fechamento orgânico. Crie ritual diário de 1 hora: 15 min de comentários estratégicos, 15 min de criação ou interação em DM, 15 min de inteligência (Sales Nav, alertas, gatilhos), 15 min de planejamento do dia seguinte. Disciplina diária ganha de talento esporádico todas as vezes.",
      checkItems: [
        "Bloqueie 1 hora diária na agenda só para Social Selling",
        "Use checklist diário fixo com as 4 atividades (comentar, criar, prospectar, planejar)",
        "Reporte progresso semanal em 1:1 com o gestor",
        "Recompense streaks (60 dias seguidos = bônus simbólico)",
        "Revise o playbook trimestralmente com base nas métricas e ajustes do algoritmo",
      ],
      story: `Cibele Maciel, vendedora com mais de 20 anos de experiência, sentiu na pele o impacto de manter ritmo diário em Social Selling. "Junior, seu treinamento quebrou tabus que carrego há mais de 20 anos em vendas! Foi direto, prático e profundo. Já estou aplicando o que aprendi e voando em resultados." A constância é o que separa quem trata o LinkedIn como passatempo de quem o transforma em motor de pipeline.`,
    },
  ];

  const relatedPosts = [
    {
      title: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Previsível",
      link: "/posts/pre-vendas-sdr-bdr-2026",
      image: postCoverPreVendas,
      readTime: "22 min",
    },
    {
      title: "Cold Calling em 2026: Está Morto ou Ainda Funciona?",
      link: "/posts/cold-calling-2026",
      image: postCoverColdcalling,
      readTime: "15 min",
    },
    {
      title: "Como Vender Mais em 2026: 13 Passos Práticos",
      link: "/posts/como-vender-mais-2026",
      image: postCoverComoVenderMais,
      readTime: "20 min",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Social Selling no LinkedIn em 2026: Como Gerar Pipeline Sem Cold Call",
    description:
      "Guia completo de Social Selling no LinkedIn em 2026. Perfil, Sales Navigator, conteúdo, engajamento, DM, eventos, métricas e integração ao CRM em 13 passos práticos.",
    author: {
      "@type": "Person",
      name: "Junior Bianchim",
      jobTitle: "Especialista em Treinamento de Equipes Comerciais",
    },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-04-22",
    dateModified: "2026-04-22",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/social-selling-linkedin-2026",
    },
    image: "https://bianchimbusiness.com.br/og-social-selling-linkedin-2026.jpg",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "O que é Social Selling no LinkedIn?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Social Selling é o uso estratégico de redes sociais profissionais — principalmente o LinkedIn — para construir autoridade, gerar relacionamento com decisores e abrir oportunidades comerciais sem depender de cold call ou e-mail frio. Combina conteúdo, engajamento e prospecção contextual.",
        },
      },
      {
        "@type": "Question",
        name: "Quanto tempo por dia é preciso investir em Social Selling no LinkedIn?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "O ideal são 60 minutos diários divididos em quatro blocos de 15 min: comentários estratégicos, criação ou DMs, inteligência via Sales Navigator e planejamento do dia seguinte. Disciplina diária gera mais resultado do que sprints esporádicos.",
        },
      },
      {
        "@type": "Question",
        name: "Vale a pena pagar pelo LinkedIn Sales Navigator em 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Sim, desde que usado para alertas de gatilhos, listas dinâmicas e TeamLink. Quem usa só para busca avançada não justifica o investimento. Bem aplicado, paga-se em uma única reunião qualificada que vire negócio.",
        },
      },
      {
        "@type": "Question",
        name: "Quantos posts devo publicar por semana no LinkedIn?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Três posts por semana, em dias e horários fixos, é a frequência que mais favorece o algoritmo em 2026. Consistência supera volume. Postar todo dia por uma semana e sumir por um mês destrói alcance.",
        },
      },
      {
        "@type": "Question",
        name: "Quais métricas acompanhar no Social Selling?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "As quatro principais são: Social Selling Index (SSI), taxa de aceite de conexão, reply rate em DM e pipeline gerado em valor. Métricas de vaidade como visualizações e likes não substituem pipeline real.",
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Social Selling no LinkedIn em 2026: Gere Pipeline Sem Cold Call"
        description="Guia completo de Social Selling no LinkedIn em 2026. Perfil, Sales Navigator, conteúdo, engajamento, DM, eventos, métricas e integração ao CRM em 13 passos práticos."
        keywords="social selling, linkedin, prospecção linkedin, sales navigator, social selling 2026, vendas linkedin, prospecção digital, conteúdo b2b, SSI, networking comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/social-selling-linkedin-2026"
        ogType="article"
        articlePublishedTime="2026-04-22"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverSocialSelling}
                  alt="Profissional usando LinkedIn em notebook para prospecção B2B representando estratégia de Social Selling em 2026"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Prospecção Digital
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Social Selling no LinkedIn em 2026:{" "}
                    <span className="text-gradient-brand">
                      Como Gerar Pipeline Sem Cold Call
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>24 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>22 Abr 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">E se você pudesse encher seu pipeline sem fazer uma única ligação fria?</strong> Em 2026, com decisor B2B blindado por filtros de e-mail e fadiga de cold call, o LinkedIn virou o canal mais previsível para gerar reunião qualificada. Quem domina Social Selling fecha mais com menos esforço.
                    </p>
                    <p>
                      O problema é que 90% dos vendedores usam o LinkedIn errado: convite em branco, pitch no primeiro DM, post motivacional genérico e zero estratégia. O resultado é previsível — taxa de aceite baixíssima, conversa zero, e a sensação de que <strong className="text-foreground">"o LinkedIn não funciona"</strong>. Funciona. Você é que não está fazendo o que precisa ser feito.
                    </p>
                    <p>
                      Este guia é o playbook que aplico em equipes de empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros, Zentai Benefícios e Triplos Tecnologia. <strong className="text-foreground">São 13 passos práticos para transformar seu perfil em motor de pipeline previsível — sem mídia paga, sem cold call, sem desespero.</strong>
                    </p>
                    <Card className="p-6 border-primary/20 bg-primary/5 my-8">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-foreground mb-2">Antes de continuar, responda honestamente:</p>
                          <p className="text-muted-foreground">
                            Olhe para o seu LinkedIn agora. <strong className="text-foreground">Se um decisor do seu ICP visitar seu perfil neste momento, ele vai ver uma página que vende — ou um currículo digital esquecido?</strong> Essa pergunta decide se Social Selling vai funcionar para você.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* LinkedIn image */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postLinkedin}
                      alt="Pessoa acessando perfil profissional no LinkedIn em smartphone representando prospecção via Social Selling"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* The 13 Steps */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    Os 13 Passos Para Dominar Social Selling no LinkedIn em 2026
                  </h2>
                  <p className="text-muted-foreground mb-10">
                    Cada passo é um diagnóstico + ação + checklist. Implemente em sequência ou ataque primeiro o que dói mais.
                  </p>

                  <div className="space-y-12">
                    {checklist.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div key={index} id={`passo-${item.number}`}>
                          <Card className="p-6 sm:p-8 border-border hover:border-primary/30 transition-all">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-extrabold text-xl">{item.number}</span>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-xl sm:text-2xl font-bold text-foreground">{item.title}</h3>
                              </div>
                              <Icon className="w-6 h-6 text-primary flex-shrink-0 hidden sm:block" />
                            </div>

                            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-destructive flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-4 h-4" />
                                Diagnóstico
                              </p>
                              <p className="text-muted-foreground">{item.problem}</p>
                            </div>

                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-primary flex items-center gap-2 mb-1">
                                <Zap className="w-4 h-4" />
                                Ação Imediata
                              </p>
                              <p className="text-muted-foreground">{item.action}</p>
                            </div>

                            <div className="space-y-3 mb-6">
                              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-primary" />
                                Checklist de Implementação
                              </p>
                              {item.checkItems.map((check, i) => (
                                <div key={i} className="flex items-start gap-3 pl-2">
                                  <div className="w-5 h-5 rounded border-2 border-primary/40 flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-muted-foreground">{check}</span>
                                </div>
                              ))}
                            </div>

                            {item.story && (
                              <div className="bg-muted/30 rounded-xl p-5 border-l-4 border-primary">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                  <Star className="w-4 h-4 text-primary" />
                                  Case Real
                                </p>
                                <p className="text-sm text-muted-foreground leading-relaxed italic">
                                  {item.story}
                                </p>
                              </div>
                            )}
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Conteudo image */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postConteudo}
                      alt="Criação de conteúdo estratégico para LinkedIn em ambiente de trabalho moderno representando autoridade digital em vendas B2B"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Summary Checklist */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    Resumo: O Mapa Completo do Social Selling no LinkedIn
                  </h2>
                  <Card className="p-6 sm:p-8 border-primary/20">
                    <div className="space-y-4">
                      {checklist.map((item, index) => (
                        <a
                          key={index}
                          href={`#passo-${item.number}`}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{item.number}</span>
                          </div>
                          <span className="text-foreground font-medium group-hover:text-primary transition-colors flex-1">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </a>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Social Selling Não Substitui Cold Call — Multiplica o Que Você Já Faz
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      O erro mais comum é tratar Social Selling como substituto da prospecção tradicional. Não é. É <strong className="text-foreground">camada de aquecimento</strong> que faz toda a operação comercial funcionar melhor: cold call vira warm call, e-mail vira reply rate maior, reunião vira show-up rate elevado.
                    </p>
                    <p>
                      Perfil afiado. ICP claro. Sales Navigator estratégico. Convite contextual. Conteúdo constante. Engajamento inteligente. DM com 3:1. Eventos como atalho. Social Listening. Métricas reais. Integração ao CRM. Disciplina diária. <strong className="text-foreground">Quando esses 13 elementos rodam juntos, o LinkedIn deixa de ser rede social e vira motor de pipeline.</strong>
                    </p>
                    <p>
                      O decisor que você quer alcançar está online agora. A única pergunta é: ele vai te ver como referência confiável quando o gatilho de compra chegar, ou como mais um vendedor invasivo que tentou empurrar produto antes de construir relação?
                    </p>
                    <p>
                      <strong className="text-foreground">Comece pelo passo 01 hoje. Implemente um passo por semana. Em 90 dias, seu LinkedIn vai parecer de outra pessoa — e seu pipeline também.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <Link key={index} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title} - conteúdo complementar sobre vendas e prospecção`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em treinamento de equipes comerciais e Social Selling"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Treine Social Selling Com IA</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, sua equipe pratica abordagens no LinkedIn, qualificação e DM em simulações reais com inteligência artificial.
                      Feedback instantâneo, gamificação e métricas claras.
                      <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default SocialSellingLinkedIn2026;
